import React, { useEffect, useState } from "react";
import { API_BASE } from "../../api/config";
import { modalBox, modalInput, modalLabel, modalPrimary } from "./FolgevertragModal";

/**
 * Welle 109: Wohnungsübergabe-Protokoll (Einzug/Auszug) — Formular zum Nachbau der
 * Papiervorlage. Alles aus der Datenbank ist vorbelegt (Adresse, Mieter, Termin, Räume,
 * Zählernummern; beim Auszug die Schlüssel aus dem Einzug) und bleibt änderbar.
 * Nach dem Rücklauf lassen sich abgelesene Stände, Schlüssel und Mängel nachtragen.
 *
 * Props: mietvertragNr, art ("einzug"|"auszug"), kuendigungId, onClose(), onErzeugt(info)
 */
const API = `${API_BASE}/mietverwaltung/api/vorgang`;
const SCHLUESSEL = [["haus", "Haus"], ["wohnung", "Wohnung"], ["zimmer", "Zimmer"], ["balkon", "Balkontür"],
  ["keller", "Keller"], ["boden", "Boden"], ["briefkasten", "Briefkasten"]];

const klein = { fontSize: 12, color: "#94a3b8" };
const zelle = { ...modalInput, height: 30, fontSize: 13 };
const abschnitt = { marginTop: 16, paddingTop: 12, borderTop: "1px solid #334155" };

function DbMarke({ an }) {
  return an ? <span title="kommt aus der Datenbank" style={{ fontSize: 10.5, fontWeight: 700, color: "#86efac",
    border: "1px solid #166534", borderRadius: 4, padding: "0 4px", marginLeft: 6 }}>DB</span> : null;
}

export default function UebergabeprotokollForm({ mietvertragNr, art, kuendigungId, onClose, onErzeugt }) {
  const [d, setD] = useState(null);            // Formularstand
  const [db, setDb] = useState(null);          // reine Vorbelegung aus der Datenbank
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");

  useEffect(() => {
    const q = new URLSearchParams({ mietvertrag_nr: mietvertragNr, art });
    if (kuendigungId) q.set("kuendigung_id", kuendigungId);
    fetch(`${API}/protokoll?${q}`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || "Laden fehlgeschlagen"); return j; })
      .then((j) => { setD(j.daten); setDb(j.db); })
      .catch((e) => setFehler(String(e.message || e)));
  }, [mietvertragNr, art, kuendigungId]);

  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setListe = (k, i, feld, v) => setD((x) => ({ ...x, [k]: x[k].map((z, j) => (j === i ? { ...z, [feld]: v } : z)) }));

  const speichern = async () => {
    setBusy(true); setFehler(""); setMeldung("");
    try {
      const r = await fetch(`${API}/protokoll`, { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mietvertrag_nr: mietvertragNr, art, kuendigung_id: kuendigungId || null, daten: d }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Speichern fehlgeschlagen");
      setMeldung("Gespeichert.");
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const pdf = async (ablegen) => {
    setBusy(true); setFehler(""); setMeldung("");
    try {
      const r = await fetch(`${API}/protokoll/pdf?ablegen=${ablegen ? 1 : 0}`, { method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mietvertrag_nr: mietvertragNr, art, kuendigung_id: kuendigungId || null, daten: d }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (ablegen) {
        const pfad = r.headers.get("X-Ablage-Pfad");
        const key = r.headers.get("X-Dokument-Key");
        const info = { pfad: pfad ? decodeURIComponent(pfad) : null, key };
        setMeldung(`Erzeugt${info.pfad ? ` und abgelegt: ${info.pfad}` : ""} · Dokumenten-Key ${key}`);
        onErzeugt?.(info);
      }
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const istDb = (feld, wert) => db && String(db[feld] ?? "") !== "" && String(db[feld]) === String(wert ?? "");

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300 }}
         onClick={onClose}>
      <div style={{ ...modalBox, width: "min(900px, 96vw)", maxHeight: "92vh" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Übergabeprotokoll {art === "einzug" ? "vor dem Einzug" : "vor dem Auszug"} — {mietvertragNr}</h3>
        <p style={{ ...klein, margin: "6px 0 0" }}>
          Vorbelegt aus der Datenbank (<DbMarke an />); alles ist änderbar. Beim Erzeugen bekommt jede Seite einen
          QR-Code — kommt das unterschriebene Protokoll als Scan zurück, wird es automatisch zugeordnet.
        </p>
        {!d && !fehler && <p style={klein}>Lädt…</p>}
        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
        {d && (
          <>
            {/* Kopf */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "8px 12px", marginTop: 12 }}>
              <label style={modalLabel}>Adresse des Mietobjekts <DbMarke an={istDb("adresse", d.adresse)} />
                <input style={modalInput} value={d.adresse || ""} onChange={(e) => set("adresse", e.target.value)} />
              </label>
              <label style={modalLabel}>Datum <DbMarke an={istDb("datum", d.datum)} />
                <input style={modalInput} value={d.datum || ""} placeholder="TT.MM.JJJJ" onChange={(e) => set("datum", e.target.value)} />
              </label>
              <label style={modalLabel}>von (Uhr)
                <input style={modalInput} value={d.von || ""} placeholder="09:00" onChange={(e) => set("von", e.target.value)} />
              </label>
              <label style={modalLabel}>bis (Uhr)
                <input style={modalInput} value={d.bis || ""} placeholder="10:00" onChange={(e) => set("bis", e.target.value)} />
              </label>
            </div>
            <div style={{ marginTop: 10 }}>
              <span style={modalLabel}>Mieter (je eine Unterschriftszeile)</span>
              {(d.mieter || []).map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <input style={{ ...zelle, flex: 1 }} value={m} onChange={(e) => set("mieter", d.mieter.map((x, j) => (j === i ? e.target.value : x)))} />
                  <button className="me-btn me-btn-outline" onClick={() => set("mieter", d.mieter.filter((_, j) => j !== i))}>entfernen</button>
                </div>
              ))}
              <button className="me-btn me-btn-outline" style={{ marginTop: 6, fontSize: 12 }}
                onClick={() => set("mieter", [...(d.mieter || []), ""])}>+ Mieter</button>
              {db && (db.mieter || []).length > 0 && (db.mieter || []).every((n) => !n.includes(" ")) && (
                <small style={{ ...klein, display: "block", marginTop: 4 }}>
                  In den Stammdaten stehen nur Nachnamen — Vornamen bitte hier ergänzen.
                </small>
              )}
            </div>

            {/* Räume */}
            <div style={abschnitt}>
              <b>Mängel je Raum</b>
              <div style={{ display: "flex", gap: 16, margin: "6px 0 8px", fontSize: 13 }}>
                <span>Es wurden</span>
                {[["", "noch offen"], ["keine", "keine Mängel festgestellt"], ["folgende", "folgende Mängel festgestellt"]].map(([w, l]) => (
                  <label key={w} style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    <input type="radio" checked={(d.maengel_gesamt || "") === w} onChange={() => set("maengel_gesamt", w)} /> {l}
                  </label>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "34px 1.3fr 58px 1.5fr 1.5fr", gap: "4px 8px", alignItems: "center", fontSize: 12.5 }}>
                <span style={klein} title="Zeile aufs Formular">drauf</span><span style={klein}>Raum</span>
                <span style={klein}>keine M.</span><span style={klein}>Folgende Mängel</span><span style={klein}>Anmerkungen</span>
                {(d.raeume || []).map((r, i) => (
                  <React.Fragment key={i}>
                    <input type="checkbox" checked={!!r.aktiv} onChange={(e) => setListe("raeume", i, "aktiv", e.target.checked)} />
                    <input style={{ ...zelle, opacity: r.aktiv ? 1 : 0.45 }} value={r.name} onChange={(e) => setListe("raeume", i, "name", e.target.value)} />
                    <input type="checkbox" checked={!!r.keine_maengel} disabled={!r.aktiv} onChange={(e) => setListe("raeume", i, "keine_maengel", e.target.checked)} />
                    <input style={zelle} value={r.maengel || ""} disabled={!r.aktiv} onChange={(e) => setListe("raeume", i, "maengel", e.target.value)} />
                    <input style={zelle} value={r.anmerkung || ""} disabled={!r.aktiv} onChange={(e) => setListe("raeume", i, "anmerkung", e.target.value)} />
                  </React.Fragment>
                ))}
              </div>
              <button className="me-btn me-btn-outline" style={{ marginTop: 6, fontSize: 12 }}
                onClick={() => set("raeume", [...(d.raeume || []), { name: "", aktiv: true, keine_maengel: false, maengel: "", anmerkung: "" }])}>
                + Raum
              </button>
              <small style={{ ...klein, display: "block", marginTop: 4 }}>
                Abgewählte Räume erscheinen nicht auf dem Blatt; das Formular hat Platz für 20 Zeilen.
              </small>
            </div>

            {/* Schlüssel */}
            <div style={abschnitt}>
              <b>Übergabe Schlüssel</b> <span style={klein}>(Anzahl)</span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginTop: 6 }}>
                {SCHLUESSEL.map(([k, l]) => (
                  <label key={k} style={modalLabel}>{l}
                    <input style={zelle} value={d.schluessel?.[k] || ""} onChange={(e) => set("schluessel", { ...d.schluessel, [k]: e.target.value })} />
                  </label>
                ))}
              </div>
              <label style={{ ...modalLabel, marginTop: 8 }}>Es fehlen noch folgende Schlüssel
                <input style={modalInput} value={d.schluessel_fehlend || ""} onChange={(e) => set("schluessel_fehlend", e.target.value)} />
              </label>
            </div>

            {/* Zähler */}
            <div style={abschnitt}>
              <b>Zählerstände</b>
              <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.5fr 1fr 1.6fr", gap: "4px 8px", alignItems: "center", marginTop: 6, fontSize: 12.5 }}>
                <span style={klein}>Zähler</span><span style={klein}>Zählernummer</span><span style={klein}>Zählerstand</span><span style={klein}>Hinweis</span>
                {(d.zaehler || []).map((z, i) => {
                  const dbZ = (db?.zaehler || [])[i] || {};
                  return (
                    <React.Fragment key={i}>
                      <span>{z.art}</span>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input style={{ ...zelle, flex: 1 }} value={z.nummer || ""} onChange={(e) => setListe("zaehler", i, "nummer", e.target.value)} />
                        <DbMarke an={dbZ.nummer && dbZ.nummer === z.nummer} />
                      </div>
                      <input style={zelle} value={z.stand || ""} placeholder="vor Ort ablesen" onChange={(e) => setListe("zaehler", i, "stand", e.target.value)} />
                      <small style={klein}>{[z.einbauort, z.hinweis].filter(Boolean).join(" · ")}</small>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Rest */}
            <div style={{ ...abschnitt, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }}>
              <label style={modalLabel}>Letzte Renovierung
                <input style={modalInput} value={d.renovierung || ""} onChange={(e) => set("renovierung", e.target.value)} />
              </label>
              <label style={modalLabel}>Ort, Datum (Unterschriften)
                <input style={modalInput} value={d.ort_datum || ""} placeholder="leer = von Hand" onChange={(e) => set("ort_datum", e.target.value)} />
              </label>
              <label style={{ ...modalLabel, gridColumn: "1 / 3" }}>Bemerkung – Sonstiges
                <input style={modalInput} value={d.bemerkung || ""} onChange={(e) => set("bemerkung", e.target.value)} />
              </label>
              <label style={modalLabel}>Zeuge: Name und Anschrift
                <input style={modalInput} value={d.zeuge || ""} onChange={(e) => set("zeuge", e.target.value)} />
              </label>
              <label style={modalLabel}>Fußzeile
                <input style={modalInput} value={d.fusszeile || ""} onChange={(e) => set("fusszeile", e.target.value)} />
              </label>
            </div>
          </>
        )}
        {meldung && <p style={{ color: "#86efac", fontSize: 13, marginTop: 10 }}>{meldung}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="me-btn me-btn-outline" onClick={onClose}>Schließen</button>
          <button className="me-btn me-btn-outline" disabled={busy || !d} onClick={speichern}>Speichern</button>
          <button className="me-btn me-btn-outline" disabled={busy || !d} onClick={() => pdf(false)}>PDF-Vorschau</button>
          <button className="me-btn" style={modalPrimary} disabled={busy || !d} onClick={() => pdf(true)}>
            {busy ? "Arbeitet…" : "PDF erzeugen + ablegen"}
          </button>
        </div>
      </div>
    </div>
  );
}
